import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useData } from "../context/DataContext";
import { useWatchlist } from "../context/WatchlistContext.jsx";
import { coinbaseSpotUrl } from "../utils/coinbaseUrl";
import { getMarketPressure } from "../utils/marketPressure";
import { stripLeadingSymbol } from "../utils/alertText";
import { normalizeEventType } from "../utils/alerts_normalize.js";
import {
  PRIORITY_FADING_MS,
  buildPriorityEvidence,
  buildPriorityItems,
} from "../utils/priorityEngine.js";
import { RowInfo, RowStar } from "./tables/RowActions.jsx";
import "../styles/alerts-tab.css";

const TYPE_LABEL = {
  moonshot: "MOONSHOT",
  crater: "CRATER",
  breakout: "BREAKOUT",
  dump: "DUMP",
  whale_move: "WHALE",
  stealth_move: "STEALTH",
  divergence: "DIVERGENCE",
  impulse_1m: "MOVE",
  impulse_3m: "MOVE",
  coin_fomo: "COIN FOMO",
  coin_breadth_thrust: "BREADTH THRUST",
  coin_breadth_failure: "BREADTH FAILURE",
  coin_reversal_up: "REVERSAL UP",
  coin_reversal_down: "REVERSAL DOWN",
  coin_fakeout: "FAKEOUT",
  coin_persistent_gainer: "PERSIST GAINER",
  coin_persistent_loser: "PERSIST LOSER",
  coin_volatility_expansion: "VOL EXPANSION",
  coin_liquidity_shock: "LIQ SHOCK",
  coin_trend_break_up: "TREND BREAK UP",
  coin_trend_break_down: "TREND BREAK DOWN",
  coin_squeeze_break: "SQUEEZE BREAK",
  coin_exhaustion_top: "EXHAUSTION TOP",
  coin_exhaustion_bottom: "EXHAUSTION BOTTOM",
  social_spike_1h: "SOCIAL SPIKE",
  engagement_surge_1h: "ENGAGEMENT SURGE",
  social_divergence: "SOCIAL DIVERGENCE",
  social_pulse: "SOCIAL PULSE",
  listing: "LISTING",
  delisting: "DELISTING",
  unlock: "UNLOCK",
  upgrade: "UPGRADE",
  hack_or_exploit: "HACK / EXPLOIT",
  governance_vote: "GOVERNANCE",
  partnership: "PARTNERSHIP",
  news_positive: "NEWS POSITIVE",
  news_negative: "NEWS NEGATIVE",
  external_event: "EXTERNAL EVENT",
  news_confirmed_breakout: "NEWS CONFIRMED",
  social_confirmed_momentum: "SOCIAL CONFIRMED",
  event_confirmed_volume: "EVENT CONFIRMED",
  fomo: "FOMO",
  fomo_alert: "FOMO",
  fear: "FEAR",
  fear_alert: "FEAR",
};

// Keyed by canonical IDs (SCREAMING_SNAKE_CASE). Looked up via normalizeEventType().
// Display labels for glossary rendering come from TYPE_DISPLAY below.
const TYPE_HELP = {
  MOONSHOT: "Fast surge",
  CRATER: "Fast drop",
  BREAKOUT: "Trend shift",
  DUMP: "Strong sell pressure",
  WHALE: "Unusual volume spike",
  STEALTH: "Volume warming quietly",
  DIVERGENCE: "Price vs sentiment mismatch",
  COIN_FOMO: "Coin acceleration with market heat context",
  BREADTH_THRUST: "Coin strength confirmed by broad participation",
  BREADTH_FAILURE: "Coin weakness in weak market breadth",
  REVERSAL_UP: "Coin reversed from sell pressure to buy pressure",
  REVERSAL_DOWN: "Coin reversed from buy pressure to sell pressure",
  FAKEOUT: "Breakout trap rejected quickly",
  PERSIST_GAINER: "Coin stayed in sustained upside streak",
  PERSIST_LOSER: "Coin stayed in sustained downside streak",
  VOL_EXPANSION: "Coin volatility expanded vs recent baseline",
  LIQ_SHOCK: "Volume surged while price stayed muted",
  TREND_BREAK_UP: "Fast/slow trend crossover with volume support",
  TREND_BREAK_DOWN: "Fast/slow trend rollover with volume support",
  SQUEEZE_BREAK: "Compression regime broke into a sharp move",
  EXHAUSTION_TOP: "Upside run lost energy and flipped",
  EXHAUSTION_BOTTOM: "Downside run lost energy and snapped back",
  SOCIAL_SPIKE: "Social attention accelerated quickly",
  ENGAGEMENT_SURGE: "Participation expanded beyond baseline",
  SOCIAL_DIVERGENCE: "Social direction disagrees with tape direction",
  SOCIAL_PULSE: "Social timeline activity is live",
  LISTING: "Major listing catalyst detected",
  DELISTING: "Delisting risk or removal notice",
  UNLOCK: "Token unlock / vesting pressure",
  UPGRADE: "Protocol upgrade event",
  HACK_EXPLOIT: "Security incident / exploit risk",
  GOVERNANCE: "Governance proposal or vote event",
  PARTNERSHIP: "Partnership or integration event",
  NEWS_POSITIVE: "Bullish external news catalyst",
  NEWS_NEGATIVE: "Bearish external news catalyst",
  EXTERNAL_EVENT: "External event without directional bias",
  NEWS_CONFIRMED: "News and tape action aligned",
  SOCIAL_CONFIRMED: "Social momentum and tape aligned",
  EVENT_CONFIRMED: "Event and volume expansion aligned",
  MOVE: "Short-term move",
  FOMO: "Chasing behavior",
  FEAR: "Risk-off behavior",
};

// Human-friendly display labels for canonical IDs that differ from their identifier.
// Used only for the glossary; rendering of live alert rows uses toUpperType() unchanged.
const TYPE_DISPLAY = {
  COIN_FOMO: "COIN FOMO",
  BREADTH_THRUST: "BREADTH THRUST",
  BREADTH_FAILURE: "BREADTH FAILURE",
  REVERSAL_UP: "REVERSAL UP",
  REVERSAL_DOWN: "REVERSAL DOWN",
  PERSIST_GAINER: "PERSIST GAINER",
  PERSIST_LOSER: "PERSIST LOSER",
  VOL_EXPANSION: "VOL EXPANSION",
  LIQ_SHOCK: "LIQ SHOCK",
  TREND_BREAK_UP: "TREND BREAK UP",
  TREND_BREAK_DOWN: "TREND BREAK DOWN",
  SQUEEZE_BREAK: "SQUEEZE BREAK",
  EXHAUSTION_TOP: "EXHAUSTION TOP",
  EXHAUSTION_BOTTOM: "EXHAUSTION BOTTOM",
  SOCIAL_SPIKE: "SOCIAL SPIKE",
  ENGAGEMENT_SURGE: "ENGAGEMENT SURGE",
  SOCIAL_DIVERGENCE: "SOCIAL DIVERGENCE",
  SOCIAL_PULSE: "SOCIAL PULSE",
  HACK_EXPLOIT: "HACK / EXPLOIT",
  NEWS_POSITIVE: "NEWS POSITIVE",
  NEWS_NEGATIVE: "NEWS NEGATIVE",
  EXTERNAL_EVENT: "EXTERNAL EVENT",
  NEWS_CONFIRMED: "NEWS CONFIRMED",
  SOCIAL_CONFIRMED: "SOCIAL CONFIRMED",
  EVENT_CONFIRMED: "EVENT CONFIRMED",
};

const CONTEXT_BADGE_TRANSLATIONS = {
  "BREAKING AWAY": "moving independently from the market",
  "MARKET CARRIED": "move follows the wider market",
  "SPOT BUYING": "buyers stepping in",
  "SPOT SELLING": "selling flow",
  "THIN AIR": "thin liquidity",
  "WIDE SPREAD": "wide spread",
};

const ALERT_TABS = [
  { key: "ALL", label: "All" },
  { key: "WATCHLIST", label: "Watchlist" },
  { key: "MOONSHOT", label: "Moonshot" },
  { key: "BULLISH", label: "Bullish" },
  { key: "HEATING", label: "Heating Up" },
  { key: "WHALE", label: "Whale" },
  { key: "DUMP", label: "Dump" },
  { key: "BREAKOUT", label: "Breakout" },
  { key: "LIQ", label: "Liq Shock" },
];

const marketMoodKey = (a) => {
  const mood = String(a?.evidence?.mood || a?.direction || "").toLowerCase();
  return mood === "fear" ? "FEAR" : "FOMO";
};

const tabKeyForAlert = (a) => {
  const raw = String(a?.type_key || a?.type || "").toLowerCase();
  if (!raw) return "ALL";
  if (raw.includes("social_") || raw.includes("engagement") || raw.includes("sentiment")) return "SOCIAL";
  if (raw.includes("news_")) return "NEWS";
  if (raw.includes("listing") || raw.includes("delisting") || raw.includes("unlock") || raw.includes("upgrade") || raw.includes("governance") || raw.includes("partnership") || raw.includes("hack") || raw.includes("external_event")) return "EVENTS";
  if (raw.includes("funding") || raw.includes("open_interest") || raw.includes("liquidation") || raw.includes("derivative") || raw === "oi_spike") return "DERIV";
  if (raw.includes("moonshot")) return "MOONSHOT";
  if (raw.includes("breakout")) return "BREAKOUT";
  if (raw.includes("crater")) return "DUMP";
  if (raw.includes("whale")) return "WHALE";
  if (raw.includes("stealth")) return "WHALE";
  if (raw.includes("dump")) return "DUMP";
  if (raw.includes("coin_fomo")) return "COIN_FOMO";
  if (raw.includes("coin_breadth_thrust")) return "THRUST";
  if (raw.includes("coin_breadth_failure")) return "FAILURE";
  if (raw.includes("coin_reversal")) return "REVERSAL";
  if (raw.includes("coin_fakeout")) return "FAKEOUT";
  if (raw.includes("coin_persistent")) return "PERSIST";
  if (raw.includes("coin_volatility_expansion")) return "VOLX";
  if (raw.includes("coin_liquidity_shock")) return "LIQ";
  if (raw.includes("coin_trend_break")) return "TREND";
  if (raw.includes("coin_squeeze_break")) return "SQUEEZE";
  if (raw.includes("coin_exhaustion")) return "EXHAUST";
  if (raw.includes("fear") || raw.includes("fomo")) return marketMoodKey(a);
  if (raw.includes("divergence")) return "ALL";
  if (raw.includes("impulse")) return "IMPULSE";
  return "ALL";
};

const rawTypeKey = (a) => String(a?.type_key || a?.type || "").toLowerCase();

const HEATING_TYPE_TOKENS = [
  "coin_fomo",
  "coin_breadth_thrust",
  "coin_persistent_gainer",
  "coin_trend_break_up",
  "coin_squeeze_break",
  "breakout",
];

const BULLISH_TYPE_TOKENS = [
  ...HEATING_TYPE_TOKENS,
  "moonshot",
  "coin_reversal_up",
  "market_fomo_siren",
  "fomo",
];

const isHeatingAlert = (a) => {
  const raw = rawTypeKey(a);
  if (!raw) return false;
  if (HEATING_TYPE_TOKENS.some((token) => raw.includes(token))) return true;
  const heat = Number(a?.evidence?.heat ?? a?.evidence?.mood_index ?? null);
  const pct = Number(pickPct(a));
  return Number.isFinite(heat) && heat >= 65 && Number.isFinite(pct) && pct > 0;
};

const isBullishAlert = (a) => {
  const raw = rawTypeKey(a);
  if (!raw) return false;
  if (raw.includes("fear") || raw.includes("crater") || raw.includes("dump")) return false;
  if (BULLISH_TYPE_TOKENS.some((token) => raw.includes(token))) return true;
  const pct = Number(pickPct(a));
  return Number.isFinite(pct) && pct > 0.75;
};

const isWhaleAlert = (a) => {
  const raw = rawTypeKey(a);
  if (!raw) return false;
  return (
    raw.includes("whale") ||
    raw.includes("stealth") ||
    raw.includes("liquidity_shock")
  );
};

const isDumpAlert = (a) => {
  const raw = rawTypeKey(a);
  if (!raw) return false;
  if (raw.includes("dump") || raw.includes("crater") || raw.includes("fear")) return true;
  if (
    raw.includes("persistent_loser") ||
    raw.includes("trend_break_down") ||
    raw.includes("reversal_down") ||
    raw.includes("exhaustion_top") ||
    raw.includes("breadth_failure")
  ) {
    return true;
  }
  const pct = Number(pickPct(a));
  return Number.isFinite(pct) && pct < -0.75;
};

const alertMatchesTab = (a, tabKey) => {
  if (tabKey === "ALL") return true;
  if (tabKey === "WATCHLIST") return true;
  if (tabKey === "HEATING") return isHeatingAlert(a);
  if (tabKey === "BULLISH") return isBullishAlert(a);
  if (tabKey === "WHALE") return isWhaleAlert(a);
  if (tabKey === "DUMP") return isDumpAlert(a);
  return tabKeyForAlert(a) === tabKey;
};

const SEV_RANK = { critical: 5, high: 4, medium: 3, low: 2, info: 1 };
const PRIORITY_WINDOW_MS = 7 * 60 * 1000;
const toUpperType = (alert) => {
  const k = String(alert?.type_key || alert?.type || "").toLowerCase();
  if (k.startsWith("coin_")) {
    return TYPE_LABEL[k] || k.toUpperCase();
  }
  if (k.includes("fear") || k.includes("fomo")) {
    return marketMoodKey(alert);
  }
  return TYPE_LABEL[k] || k.toUpperCase() || "ALERT";
};

const toEpochMs = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const normalizeNumericTs = (raw) => {
    if (!Number.isFinite(raw)) return null;
    const abs = Math.abs(raw);
    if (abs < 1e11) return Math.round(raw * 1000); // seconds
    if (abs < 1e14) return Math.round(raw); // milliseconds
    if (abs < 1e17) return Math.round(raw / 1000); // microseconds
    return Math.round(raw / 1e6); // nanoseconds-ish
  };

  if (typeof value === "number") {
    return normalizeNumericTs(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^-?\d+(?:\.\d+)?$/.test(raw)) {
    return normalizeNumericTs(Number(raw));
  }

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickTsMs = (a) => {
  if (!a || typeof a !== "object") return null;
  const fields = [
    a.event_ts_ms,
    a.ts_ms,
    a.timestamp_ms,
    a.event_ts,
    a.ts,
    a.timestamp,
    a.created_at,
    a.createdAt,
    a.time,
    a.when,
    a.date,
  ];
  for (const value of fields) {
    const ms = toEpochMs(value);
    if (Number.isFinite(ms)) return ms;
  }
  return null;
};

const formatAlertTimestamp = (a) => {
  const tsMs = pickTsMs(a);
  if (!Number.isFinite(tsMs)) return null;
  const d = new Date(tsMs);
  if (!Number.isFinite(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

const ageLabel = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "\u2014";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

const pickPct = (a) => {
  const ev = a?.evidence || {};
  const pct =
    ev.pct_1m ??
    ev.pct_3m ??
    ev.pct_5m ??
    ev.pct_15m ??
    ev.pct_1h ??
    a?.pct ??
    a?.magnitude ??
    null;
  const n = Number(pct);
  return Number.isFinite(n) ? n : null;
};

const pickVolPct = (a) => {
  const ev = a?.evidence || {};
  const v = ev.volume_change_1h_pct ?? ev.vol_change_1h_pct ?? ev.vol_pct ?? null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const shouldShowVolPct = (volPct) => Number.isFinite(volPct) && Math.abs(volPct) >= 0.01;

const toProductId = (a) => {
  let p = String(a?.product_id || a?.symbol || "").trim().toUpperCase();
  if (!p) return "";
  if (!p.includes("-")) p = `${p}-USD`;
  return p;
};

const alertSymbol = (a) =>
  String(a?.symbol || a?.product_id || "").toUpperCase().replace(/-USD$|-USDT$|-PERP$/i, "");

const sentimentSymbolForAlert = (a) => {
  const candidates = [
    a?.symbol,
    a?.product_id,
    a?.productId,
    a?.coin,
    a?.pair,
    a?.asset,
  ];
  for (const value of candidates) {
    const raw = String(value || "").trim().toUpperCase();
    if (!raw) continue;
    return raw.replace(/-USD$|-USDT$|-PERP$/i, "");
  }
  return "";
};

const pickWatchPrice = (a, latestBySymbol = {}) => {
  const ev = a?.evidence || {};
  const symbol = sentimentSymbolForAlert(a);
  const productId = toProductId(a);
  const latestCandidates = [
    latestBySymbol?.[symbol],
    latestBySymbol?.[productId],
  ].filter(Boolean);

  const candidates = [
    ev.price_now,
    ev.current_price,
    ev.price,
    a?.price,
    a?.current_price,
    ...latestCandidates.flatMap((row) => [
      row?.price,
      row?.current_price,
      row?.last_price,
      row?.close,
      typeof row === "number" ? row : null,
    ]),
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const moodFromHeat = (heat) => {
  if (!Number.isFinite(heat)) return { mood: "Unavailable", tone: "neutral", detail: "Market pressure has not been measured yet." };
  if (heat >= 67) return { mood: "Bullish", tone: "bull", detail: "Buy pressure is leading right now." };
  if (heat <= 33) return { mood: "Bearish", tone: "bear", detail: "Sell pressure is leading right now." };
  return { mood: "Neutral", tone: "neutral", detail: "Pressure is balanced. Signals may chop." };
};

export const freshnessFromStale = (priceStale, volStale) => {
  const p = Number.isFinite(priceStale) ? priceStale : null;
  const v = Number.isFinite(volStale) ? volStale : null;
  const worst = Math.max(p ?? 0, v ?? 0);
  if (p == null && v == null) return { label: "Feed freshness unavailable", tone: "unknown", hint: "Data freshness unavailable." };
  return { label: `Feed freshness: ${worst.toFixed(1)}s`, tone: "neutral", hint: `Oldest market input is ${worst.toFixed(1)}s old.` };
};

const priorityReasons = (entry) => {
  const reasons = [];
  if (entry.confirms > 1) {
    reasons.push(`${entry.confirms} confirms`);
  }
  if (entry.rankSummary) {
    reasons.push(entry.rankSummary);
  }
  if (entry.volumeAligned) {
    reasons.push("volume aligned");
  }
  if (entry.breadthSupport >= 0.62) {
    reasons.push("breadth aligned");
  } else if (entry.rankTrend === "slipping") {
    reasons.push("rank slipping");
  } else if (entry.noConfirmMs >= PRIORITY_FADING_MS) {
    reasons.push(`no confirm in ${(entry.noConfirmMs / 60000).toFixed(1)}m`);
  } else {
    reasons.push(`fresh ${ageLabel(Math.max(0, entry.nowMs - entry.lastTsMs))} ago`);
  }
  return reasons.slice(0, 3);
};

const priorityToneForBucket = (bucket) => {
  if (bucket === "bullish") return "bullish";
  if (bucket === "bearish") return "bearish";
  return "divergence";
};

const priorityStateTone = (label, bucket) => {
  if (label === "Reversal Risk" || label === "Fading") return "divergence";
  return priorityToneForBucket(bucket);
};

const deriveTapeState = (items, marketPressure) => {
  const activeLeaders = items.filter((item) => item.score >= 55).length;
  const weakening = items.filter((item) => ["Fading", "Reversal Risk"].includes(item.stateLabel)).length;
  const freshConfirms = items.reduce((sum, item) => sum + item.freshConfirms, 0);
  const breadth = Number(marketPressure?.components?.breadth ?? 0);

  let tapeState = "Choppy";
  if (weakening >= 2 && activeLeaders <= 1) tapeState = "Reversing";
  else if (activeLeaders >= 3 && freshConfirms >= 3 && breadth >= 0.56) tapeState = "Expanding";
  else if (activeLeaders <= 2 && items.some((item) => item.score >= 70)) tapeState = "Concentrated";
  else if (weakening >= 1 && freshConfirms < 2) tapeState = "Cooling";

  return {
    tapeState,
    freshConfirms,
    activeLeaders,
    weakening,
  };
};

const filterAlertRows = (rows, { coinFilter = "ALL", typeTab = "ALL", sev = "ALL", watchHas = null }) => {
  let out = Array.isArray(rows) ? rows : [];
  if (coinFilter !== "ALL") {
    out = out.filter((a) => alertSymbol(a) === coinFilter);
  }
  if (typeTab === "WATCHLIST" && watchHas) {
    out = out.filter((a) => watchHas(sentimentSymbolForAlert(a)));
  } else if (typeTab !== "ALL") {
    out = out.filter((a) => alertMatchesTab(a, typeTab));
  }
  if (sev !== "ALL") {
    out = out.filter((a) => String(a?.severity || "info").toUpperCase() === sev);
  }
  return out;
};

const sortAlertRows = (rows, { sort = "IMPORTANCE", feed = "ACTIVE" }) => {
  const out = Array.isArray(rows) ? [...rows] : [];
  if (sort === "URGENCY") {
    return out.sort((a, b) =>
      (SEV_RANK[String(b?.severity || "info").toLowerCase()] || 0) -
      (SEV_RANK[String(a?.severity || "info").toLowerCase()] || 0)
    );
  }
  if (sort === "MAGNITUDE") {
    return out.sort((a, b) => Math.abs(pickPct(b) ?? 0) - Math.abs(pickPct(a) ?? 0));
  }
  if (sort === "TIME") {
    return out.sort((a, b) => (pickTsMs(b) || 0) - (pickTsMs(a) || 0));
  }
  if (feed === "SIGNALS") {
    return out.sort((a, b) => {
      const confidenceDelta = Number(b?.confidence ?? 0) - Number(a?.confidence ?? 0);
      if (confidenceDelta) return confidenceDelta;
      return (pickTsMs(b) || 0) - (pickTsMs(a) || 0);
    });
  }
  if (feed === "ACTIVE") return out;
  return out.sort((a, b) => {
    const sb = SEV_RANK[String(b?.severity || "info").toLowerCase()] || 0;
    const sa = SEV_RANK[String(a?.severity || "info").toLowerCase()] || 0;
    if (sb !== sa) return sb - sa;
    return (pickTsMs(b) || 0) - (pickTsMs(a) || 0);
  });
};

function PriorityStrip({ items = [], nowMs, onOpenCoinSentiment = null, marketPressure = null, open = true, onToggle }) {
  const summary = deriveTapeState(items, marketPressure);

  return (
    <section className="bh-priority-strip" aria-label="Hot right now">
      <div className="bh-priority-strip__head">
        <button
          type="button"
          className="bh-section-toggle bh-priority-strip__toggle"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls="bh-priority-body"
        >
          <span className="bh-section-chevron" aria-hidden="true">{open ? "▾" : "▶"}</span>
          <span className="bh-priority-strip__title">Hot Right Now</span>
          <span className="bh-priority-strip__sub">Live shortlist</span>
        </button>
        <div className="bh-priority-strip__meta">7m model</div>
      </div>

      {open ? (
        <div id="bh-priority-body">
          <div className="bh-priority-strip__stats">
            <span>Tape: {summary.tapeState}</span>
            <span className="bh-stat-sep" aria-hidden="true">·</span>
            <span>Fresh {summary.freshConfirms}</span>
            <span className="bh-stat-sep" aria-hidden="true">·</span>
            <span>Leaders {summary.activeLeaders}</span>
            <span className="bh-stat-sep" aria-hidden="true">·</span>
            <span>Weakening {summary.weakening}</span>
          </div>

          {!items.length ? (
            <div className="bh-priority-empty">
              <div>No dominant live setup right now.</div>
            </div>
          ) : (
            <div className="bh-priority-rows">
              {items.map((item) => {
                const reasons = priorityReasons({ ...item, nowMs });
                return (
                  <button
                    key={`${item.bucket}:${item.symbol}`}
                    type="button"
                    className="bh-priority-row"
                    data-tone={priorityStateTone(item.stateLabel, item.bucket)}
                    onClick={() => onOpenCoinSentiment?.(item.symbol, { source: "priority_strip", symbol: item.symbol })}
                  >
                    <div className="bh-priority-row__top">
                      <div className="bh-priority-row__title">
                        <span className="bh-priority-row__symbol">{item.symbol}</span>
                        <span className="bh-priority-row__sep">·</span>
                        <span className="bh-priority-row__label">{item.stateLabel}</span>
                      </div>
                      <div className="bh-priority-row__score">{item.score}</div>
                    </div>
                    <div className="bh-priority-row__summary">
                      {reasons.join(" · ")}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function MarketMoodCard({ meta, variant = "full" }) {
  const compact = variant === "compact";
  const micro = variant === "micro";
  const mp = getMarketPressure({ market_pressure: meta?.market_pressure });
  const heat = mp?.index === null || mp?.index === undefined ? null : Number(mp.index);
  const hasHeat = Number.isFinite(heat);

  const stale = meta?.stale_seconds || {};
  const priceStale = Number(stale?.price);
  const volStale = Number(stale?.volume);

  const { mood, tone, detail } = moodFromHeat(heat);
  const freshness = freshnessFromStale(priceStale, volStale);

  if (micro) {
    return (
      <div className="bh-pressure-card bh-pressure-card--micro" data-tone={tone}>
        <div className="bh-pressure-micro-top">
          <span className="bh-pressure-micro-mood">{mood}</span>
        </div>
        <div className="bh-pressure-track bh-pressure-track--micro" aria-label="Market mood gauge">
          <div className="bh-pressure-fill" style={{ width: `${hasHeat ? heat : 0}%` }} />
        </div>
        <div className="bh-pressure-micro-foot">
          <span>{hasHeat ? `${heat.toFixed(0)} / 100` : "Pressure unavailable"}</span>
          <span>{freshness.label}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bh-pressure-card ${compact ? "bh-pressure-card--compact" : ""}`}
      data-tone={tone}
    >
      <div className="bh-pressure-toprow">
        <div className="bh-pressure-title">
          <span className="bh-mood-dot" aria-hidden="true" />
          Market Mood
        </div>
        <div className="bh-pressure-label">{mood}</div>
      </div>

      <div className={`bh-pressure-sub ${compact ? "bh-pressure-sub--compact" : ""}`}>{detail}</div>

      <div className="bh-pressure-track" aria-label="Market mood gauge">
        <div className="bh-pressure-fill" style={{ width: `${hasHeat ? heat : 0}%` }} />
      </div>

      <div className="bh-pressure-row">
        <div className="bh-pressure-score">{hasHeat ? `${heat.toFixed(0)} / 100` : "Pressure unavailable"}</div>
        <div className="bh-pressure-confidence" data-tone={freshness.tone}>
          {freshness.label}
        </div>
      </div>

      <div className="bh-pressure-fresh">
        <span>Price: {Number.isFinite(priceStale) ? `${priceStale.toFixed(1)}s old` : "\u2014"}</span>
        <span>Volume: {Number.isFinite(volStale) ? `${volStale.toFixed(1)}s old` : "\u2014"}</span>
      </div>

      {!compact ? <div className="bh-pressure-hint">{freshness.hint}</div> : null}
    </div>
  );
}

function ProofFooter({ meta, activeCount, recentCount }) {
  const stale = meta?.stale_seconds || {};
  const priceStale = Number(stale?.price);
  const volStale = Number(stale?.volume);

  const healthy =
    Number.isFinite(priceStale) && priceStale < 30 &&
    Number.isFinite(volStale) && volStale < 60;

  return (
    <div className="bh-proof-footer">
      <div className="bh-proof-item">
        <span className="bh-proof-key">Signals healthy</span>
        <span className={`bh-proof-val ${healthy ? "bh-proof--ok" : "bh-proof--warn"}`}>
          {healthy ? "Yes" : "Degraded"}
        </span>
      </div>
      <div className="bh-proof-item">
        <span className="bh-proof-key">Active</span>
        <span className="bh-proof-val">{activeCount}</span>
      </div>
      <div className="bh-proof-item">
        <span className="bh-proof-key">Recent</span>
        <span className="bh-proof-val">{recentCount}</span>
      </div>
      <div className="bh-proof-item">
        <span className="bh-proof-key">Data age</span>
        <span className="bh-proof-val">
          price {Number.isFinite(priceStale) ? `${priceStale.toFixed(0)}s` : "\u2014"},
          vol {Number.isFinite(volStale) ? `${volStale.toFixed(0)}s` : "\u2014"}
        </span>
      </div>
    </div>
  );
}

function EventReadContent({ eventRead, whyText, readHistory }) {
  return (
    <>
      <div className="bh-event-read__eyebrow">THE READ</div>
      <div className="bh-event-read__line">
        <span className="bh-event-read__label">{eventRead.label}</span>
        {eventRead.condition ? <span className="bh-event-read__condition">{eventRead.condition}</span> : null}
      </div>
      {eventRead.summary ? <div className="bh-event-read__summary">{eventRead.summary}</div> : null}
      {whyText ? (
        <div className="bh-event-read__why">
          <span className="bh-event-read__why-label">WHY</span>
          <span className="bh-event-read__why-text">{whyText}</span>
        </div>
      ) : null}
      {eventRead.risk_note ? <div className="bh-event-read__risk">{eventRead.risk_note}</div> : null}
      {readHistory?.label ? (
        <div className="bh-event-read__history" data-status={readHistory.status || "collecting"}>
          <span>HISTORY</span>
          <span>{readHistory.label}</span>
        </div>
      ) : null}
    </>
  );
}

function SignalRow({
  a,
  nowMs,
  confidenceSemantics = "interpretation",
  onOpenCoinSentiment = null,
  isWatchlisted = false,
  onToggleWatchlist = null,
}) {
  const eventState = String(a?.primary_state || "").trim();
  const type = eventState ? eventState.toUpperCase() : toUpperType(a);
  const typeId = normalizeEventType(rawTypeKey(a));
  const sev = String(a?.severity || "info").toLowerCase();
  const promotion = String(a?.promotion || "").toUpperCase();
  const sourceLabel = String(a?.source || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (s) => s.toUpperCase());
  const sym = String(a?.product_id || a?.symbol || "").toUpperCase().replace("-USD", "");
  const ts = pickTsMs(a);
  const absTs = formatAlertTimestamp(a);
  const age = ts ? ageLabel(nowMs - ts) : "\u2014";
  const windowLabel = String(a?.window || a?.evidence?.window || "").trim();
  const eventModifier = String(a?.modifier || "").trim();
  const interp = a?.interpretation ?? null;
  const hasInterpretation = interp?.interpretation_support_level !== 'none' && Boolean(interp?.summary);
  const interpConf = (hasInterpretation && interp?.confidence != null) ? Number(interp.confidence) : null;
  const confScore = confidenceSemantics === "interpretation" && Number.isFinite(interpConf)
    ? interpConf
    : null;
  const confidenceLabel = Number.isFinite(confScore)
    ? confScore >= 70 ? 'High' : confScore >= 45 ? 'Developing' : 'Limited'
    : null;
  const supportingFactors = hasInterpretation ? (interp.supportingFactors ?? []) : [];
  const cautionFactors = hasInterpretation ? (interp.cautionFactors ?? []) : [];
  const invalidationCondition = hasInterpretation ? String(interp.invalidationCondition ?? '').trim() : '';
  const evolution = Array.isArray(a?.evolution) ? a.evolution : [];
  const eventRead = a?.the_read && typeof a.the_read === "object" ? a.the_read : null;
  const readHistory = eventRead?.history && typeof eventRead.history === "object" ? eventRead.history : null;
  const contextBadges = Array.isArray(a?.context_badges) ? a.context_badges.slice(0, 2) : [];
  const heating = isHeatingAlert(a);
  const bullish = isBullishAlert(a);
  // Suppress eventModifier in Signal details if it duplicates an already-derived badge
  const showEventModifier = Boolean(
    eventModifier &&
    !(heating && eventModifier.toLowerCase() === 'heating') &&
    !(!heating && bullish && eventModifier.toLowerCase() === 'bullish') &&
    !(promotion && eventModifier.toLowerCase() === promotion.toLowerCase())
  );

  const whyParts = contextBadges
    .map(b => {
      const key = String(b?.label || "").toUpperCase().trim();
      if (!key) return null;
      return CONTEXT_BADGE_TRANSLATIONS[key] || key.toLowerCase();
    })
    .filter(Boolean);
  const whyText = whyParts.length ? whyParts.join(" + ") : null;

  const interpSummary = hasInterpretation ? String(interp.summary).trim() : null;
  // Build clean message without repeating coin name
  let rawMsg = String(a?.message || a?.title || TYPE_HELP[typeId] || "")
    .replace(/\s+/g, " ")
    .trim();
  // Strip leading "SYMBOL:" or "SYMBOL " patterns
  rawMsg = stripLeadingSymbol(rawMsg, sym).replace(new RegExp(`^${sym}[:\\s]+`, "i"), "");
  const detail = interpSummary || rawMsg || TYPE_HELP[typeId] || "Signal detected";

  const pct = pickPct(a);
  const volPct = pickVolPct(a);

  const pctText = pct == null ? "" : `${pct > 0 ? "+" : ""}${pct.toFixed(Math.abs(pct) < 5 ? 3 : 2)}%`;
  const volText = shouldShowVolPct(volPct) ? `Vol ${volPct > 0 ? "+" : ""}${volPct.toFixed(0)}%` : "";

  const url = a?.url || a?.trade_url || coinbaseSpotUrl({ product_id: toProductId(a), symbol: a?.symbol });
  const cls = tabKeyForAlert(a);
  const sentimentSymbol = sentimentSymbolForAlert(a) || sym;

  // Determine direction for color coding
  const direction = pct == null ? "neutral" : pct > 0 ? "up" : "down";
  const handleInfoClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!sentimentSymbol) return;
    if (typeof onOpenCoinSentiment === "function") {
      onOpenCoinSentiment(sentimentSymbol, { source: "alerts_center", alert: a });
    }
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent("openInfo", { detail: sentimentSymbol }));
    }
  };

  const handleToggleStar = () => {
    if (typeof onToggleWatchlist !== "function") return;
    onToggleWatchlist(a);
  };

  const detailContent = (() => {
    if (!pctText) return <span className="bh-signal-msg-text">{detail}</span>;
    const tokenMatch = [...detail.matchAll(/[+-]?\d+(?:\.\d+)?%/g)].find((match) => {
      const raw = Number(String(match?.[0] || "").replace("%", ""));
      return Number.isFinite(raw) && Number.isFinite(pct) && Math.abs(raw - pct) < 0.02;
    });
    if (tokenMatch) {
      const token = tokenMatch[0];
      const idx = tokenMatch.index ?? detail.indexOf(token);
      const before = detail.slice(0, idx).trimEnd();
      const after = detail.slice(idx + token.length).trimStart();
      return (
        <>
          {before ? <span className="bh-signal-msg-text">{before}</span> : null}
          <span className="bh-metric bh-metric--inline" data-direction={direction}>{token}</span>
          {after ? <span className="bh-signal-msg-text">{after}</span> : null}
        </>
      );
    }
    return (
      <>
        <span className="bh-signal-msg-text">{detail}</span>
        <span className="bh-metric bh-metric--inline" data-direction={direction}>{pctText}</span>
      </>
    );
  })();

  return (
    <div
      className="bh-signal-row"
      data-sev={sev}
      data-class={cls.toLowerCase()}
      data-direction={direction}
      role="button"
      tabIndex={0}
      onClick={() => url && window.open(url, "_blank", "noopener,noreferrer")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          url && window.open(url, "_blank", "noopener,noreferrer");
        }
      }}
      title={TYPE_HELP[typeId] ? `${type}: ${TYPE_HELP[typeId]}` : type}
    >
      <div className="bh-signal-main">
        <div className="bh-signal-meta">
          <span className="bh-signal-type">{type}</span>
          {confidenceLabel ? (
            <span className="bh-signal-confidence" title={`Score: ${Math.round(confScore)}`}>
              {confidenceLabel}
            </span>
          ) : null}
          {windowLabel ? <span className="bh-signal-window">{windowLabel}</span> : null}
        </div>

        <div className="bh-signal-headline">
          <span className="bh-signal-ticker">{sym || "\u2014"}</span>
          <div className="bh-signal-msg">{detailContent}</div>
        </div>

        {hasInterpretation && (supportingFactors.length > 0 || cautionFactors.length > 0 || invalidationCondition) ? (
          <details className="bh-signal-factors" onClick={(e) => e.stopPropagation()}>
            <summary className="bh-signal-factors__toggle">Why this read?</summary>
            <div className="bh-signal-factors__body">
              {supportingFactors.map((f, i) => <div key={i} className="bh-factor bh-factor--supporting">{f}</div>)}
              {cautionFactors.map((f, i) => <div key={i} className="bh-factor bh-factor--caution">{f}</div>)}
              {invalidationCondition ? <div className="bh-factor bh-factor--invalidation">Invalidated if: {invalidationCondition}</div> : null}
            </div>
          </details>
        ) : null}

        {eventRead ? (
          hasInterpretation ? (
            <details className="bh-event-read-wrap" onClick={(e) => e.stopPropagation()}>
              <summary className="bh-event-read-wrap__toggle">Event analysis</summary>
              <div className="bh-event-read" data-tone={eventRead.tone || "neutral"}>
                <EventReadContent eventRead={eventRead} whyText={whyText} readHistory={readHistory} />
              </div>
            </details>
          ) : (
            <div className="bh-event-read" data-tone={eventRead.tone || "neutral"} aria-label="The Read">
              <EventReadContent eventRead={eventRead} whyText={whyText} readHistory={readHistory} />
            </div>
          )
        ) : null}

        {volText ? (
          <div className="bh-signal-metrics">
            <span className="bh-metric bh-metric--vol">{volText}</span>
          </div>
        ) : null}
        {evolution.length ? (
          <div className="bh-event-evolution" aria-label="Event evolution">
            {evolution.map((step, index) => (
              <span key={`${step.ts_ms || index}:${step.to || index}`} className="bh-event-evolution__step">
                {index ? <span className="bh-event-evolution__arrow" aria-hidden="true">→</span> : null}
                <span>{step.to}</span>
              </span>
            ))}
            <span className="bh-event-evolution__count">{Number(a?.alert_count || 0)} detections</span>
          </div>
        ) : null}
        <details className="bh-signal-details" onClick={(e) => e.stopPropagation()}>
          <summary className="bh-signal-details__toggle">Signal details</summary>
          <div className="bh-signal-details__chips">
            <span className="bh-signal-sev" data-sev={sev}>{String(a?.severity || "INFO").toUpperCase()}</span>
            {promotion ? <span className="bh-signal-promo" data-promo={promotion}>{promotion}</span> : null}
            {heating ? <span className="bh-signal-bias" data-bias="heating">HEATING</span> : null}
            {!heating && bullish ? <span className="bh-signal-bias" data-bias="bullish">BULLISH</span> : null}
            {showEventModifier ? <span className="bh-signal-promo" data-promo="EVENT">{eventModifier.toUpperCase()}</span> : null}
            {sourceLabel ? <span className="bh-signal-source">{sourceLabel}</span> : null}
          </div>
        </details>
      </div>

      <div className="bh-signal-row-right">
        <div className="bh-row-actions row-actions--stack">
          <RowStar starred={Boolean(isWatchlisted)} onToggleStar={handleToggleStar} />
          <RowInfo onInfoClick={() => handleInfoClick()} />
        </div>
        <div className="bh-alert-time">
          <div className="bh-alert-time-abs">{absTs || "\u2014"}</div>
          <div className="bh-alert-time-rel">{age}</div>
        </div>
      </div>
    </div>
  );
}

export { SignalRow };

export default function AlertsTab({
  filterSymbol = null,
  compact = false,
  onOpenCoinSentiment = null,
  hideHeader = false,
  hideFoot = false,
  emptyCopy = null,
}) {
  const {
    activeAlerts = [],
    alertsRecent = [],
    pulseAlerts = [],
    signalEvents = [],
    alertsMeta = {},
    latestBySymbol = {},
    gainers_1m = [],
    gainers_3m = [],
    losers_3m = [],
    market_pressure = null,
  } = useData() || {};
  const { items: watchlistItems = [], has: watchHas, toggle: watchToggle } = useWatchlist();
  const forcedCoin = useMemo(() => {
    const raw = String(filterSymbol || "").toUpperCase().replace(/-USD$|-USDT$|-PERP$/i, "");
    return raw || null;
  }, [filterSymbol]);

  const [feed, setFeed] = useState("SIGNALS");
  const [typeTab, setTypeTab] = useState("ALL");
  const [sev, setSev] = useState("ALL");
  const [sort, setSort] = useState(() => "IMPORTANCE");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [showHelp, setShowHelp] = useState(false);
  // Default to the full market stream; symbol-specific filtering is user-driven.
  const [coinFilter, setCoinFilter] = useState(() => forcedCoin || "ALL");

  // Section disclosure state — all three live here so they share one architecture.
  // HOT RIGHT NOW always starts expanded. Watchlist + Evolving are set once after
  // alert data first arrives (alertsMeta gate) and frozen for the mount lifetime.
  const [sectionOrder, setSectionOrder] = useState("watchlist-first");
  const [priorityOpen, setPriorityOpen] = useState(true);
  const [watchlistOpen, setWatchlistOpen] = useState(true);
  const [evolvingOpen, setEvolvingOpen] = useState(true);
  const layoutInitializedRef = useRef(false);

  useEffect(() => {
    if (!forcedCoin) return;
    setCoinFilter(forcedCoin);
  }, [forcedCoin]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setSort(feed === "SIGNALS" ? "IMPORTANCE" : "TIME");
  }, [feed]);

  const fallbackForcedActive = useMemo(() => {
    if (!forcedCoin) return [];
    return (Array.isArray(activeAlerts) ? activeAlerts : []).filter((a) => alertSymbol(a) === forcedCoin);
  }, [activeAlerts, forcedCoin]);

  const fallbackForcedRecent = useMemo(() => {
    if (!forcedCoin) return [];
    return (Array.isArray(alertsRecent) ? alertsRecent : []).filter((a) => alertSymbol(a) === forcedCoin);
  }, [alertsRecent, forcedCoin]);

  const effectiveActiveAlerts = useMemo(() => {
    if (!forcedCoin) return Array.isArray(activeAlerts) ? activeAlerts : [];
    return fallbackForcedActive;
  }, [forcedCoin, activeAlerts, fallbackForcedActive]);

  const effectiveRecentAlerts = useMemo(() => {
    if (!forcedCoin) return Array.isArray(alertsRecent) ? alertsRecent : [];
    return fallbackForcedRecent;
  }, [forcedCoin, alertsRecent, fallbackForcedRecent]);

  const effectiveSignalEvents = useMemo(() => {
    const source = Array.isArray(signalEvents) && signalEvents.length
      ? signalEvents
      : effectiveActiveAlerts;
    if (!forcedCoin) return source;
    return source.filter((a) => alertSymbol(a) === forcedCoin);
  }, [forcedCoin, signalEvents, effectiveActiveAlerts]);

  const effectivePulseAlerts = useMemo(() => {
    const source = Array.isArray(pulseAlerts) && pulseAlerts.length
      ? pulseAlerts
      : effectiveRecentAlerts;
    if (!forcedCoin) return source;
    return source.filter((a) => alertSymbol(a) === forcedCoin);
  }, [forcedCoin, pulseAlerts, effectiveRecentAlerts]);

  const effectiveMeta = useMemo(() => alertsMeta || {}, [alertsMeta]);

  const source = feed === "SIGNALS" ? effectiveSignalEvents : effectivePulseAlerts;
  const effectiveCoinFilter = forcedCoin || coinFilter;

  // Build coin options from available alerts
  const coinOptions = useMemo(() => {
    if (forcedCoin) return [forcedCoin];
    const set = new Set(["ALL"]);
    for (const a of effectivePulseAlerts || []) {
      const s = alertSymbol(a);
      if (s) set.add(s);
    }
    for (const a of effectiveSignalEvents || []) {
      const s = alertSymbol(a);
      if (s) set.add(s);
    }
    return Array.from(set).sort();
  }, [forcedCoin, effectivePulseAlerts, effectiveSignalEvents]);

  const tabSeedRows = useMemo(() => {
    let out = Array.isArray(source) ? source : [];
    if (effectiveCoinFilter !== "ALL") {
      out = out.filter((a) => alertSymbol(a) === effectiveCoinFilter);
    }
    return out;
  }, [source, effectiveCoinFilter]);

  const tabCounts = useMemo(() => {
    const counts = new Map();
    for (const tab of ALERT_TABS) {
      counts.set(tab.key, 0);
    }
    counts.set("ALL", tabSeedRows.length);
    for (const a of tabSeedRows) {
      for (const tab of ALERT_TABS) {
        if (tab.key === "ALL") continue;
        if (!alertMatchesTab(a, tab.key)) continue;
        counts.set(tab.key, (counts.get(tab.key) || 0) + 1);
      }
    }
    return counts;
  }, [tabSeedRows]);

  const visibleTabs = ALERT_TABS;

  const rows = useMemo(
    () =>
      sortAlertRows(
        filterAlertRows(source, {
          coinFilter: effectiveCoinFilter,
          typeTab,
          sev,
          watchHas,
        }),
        { sort, feed }
      ),
    [source, effectiveCoinFilter, typeTab, sev, sort, feed, watchHas]
  );

  const compactRecentFallbackRows = useMemo(() => {
    return sortAlertRows(
      filterAlertRows(effectiveRecentAlerts, {
        coinFilter: effectiveCoinFilter,
        typeTab,
        sev,
        watchHas,
      }),
      { sort: "TIME", feed: "RECENT" }
    );
  }, [effectiveRecentAlerts, effectiveCoinFilter, typeTab, sev]);

  const compactFallbackToRecent =
    compact && feed === "SIGNALS" && rows.length === 0 && compactRecentFallbackRows.length > 0;
  const rowsForRender = compactFallbackToRecent ? compactRecentFallbackRows : rows;
  const displayedRows = useMemo(
    () => rowsForRender.slice(0, compact ? 8 : 8),
    [rowsForRender, compact]
  );

  const watchlistAttentionRows = useMemo(() => {
    if (compact || forcedCoin) return [];
    const filtered = filterAlertRows(effectiveSignalEvents, {
      coinFilter: effectiveCoinFilter,
      typeTab: "ALL",
      sev,
      watchHas,
    }).filter((alert) => watchHas(sentimentSymbolForAlert(alert)));
    return sortAlertRows(filtered, { sort, feed: "SIGNALS" }).slice(0, 12);
  }, [compact, forcedCoin, effectiveSignalEvents, effectiveCoinFilter, sev, sort, watchHas]);

  // Initialize section order + open states exactly once, after alert data first lands.
  // Gate: alertsMeta.fallback_from_data is set in the same batch as signalEvents,
  // so it is the narrowest correct readiness signal for these sections.
  useEffect(() => {
    if (layoutInitializedRef.current) return;
    if (alertsMeta?.fallback_from_data === undefined) return;
    layoutInitializedRef.current = true;
    const hasAttention = watchlistAttentionRows.length > 0;
    setSectionOrder(hasAttention ? "watchlist-first" : "evolving-first");
    setWatchlistOpen(hasAttention);
    setEvolvingOpen(!hasAttention && effectiveSignalEvents.length > 0);
  }, [alertsMeta, watchlistAttentionRows, effectiveSignalEvents]);

  const allAlertsTitle = forcedCoin ? `${forcedCoin} Evolution` : "Signal Evolution";

  const toggleAlertWatch = useCallback(
    (alert) => {
      const symbol = sentimentSymbolForAlert(alert);
      const price = pickWatchPrice(alert, latestBySymbol);
      if (!symbol || !Number.isFinite(price) || price <= 0) return;
      watchToggle({ symbol, price });
    },
    [watchToggle, latestBySymbol]
  );

  const boardRanks = useMemo(() => {
    const rankMap = (rows) => {
      const map = new Map();
      rows.forEach((row, index) => {
        const symbol = sentimentSymbolForAlert(row);
        if (!symbol || map.has(symbol)) return;
        const rank = Number(row?.rank ?? index + 1);
        map.set(symbol, { rank: Number.isFinite(rank) ? rank : index + 1 });
      });
      return map;
    };
    return {
      oneMin: rankMap(gainers_1m),
      gain3m: rankMap(gainers_3m),
      loss3m: rankMap(losers_3m),
    };
  }, [gainers_1m, gainers_3m, losers_3m]);

  const boardContext = useMemo(() => {
    if (!forcedCoin) return null;
    const oneMinBoard = boardRanks.oneMin.get(forcedCoin);
    const gain3Board = boardRanks.gain3m.get(forcedCoin);
    const loss3Board = boardRanks.loss3m.get(forcedCoin);
    const parts = [];
    if (oneMinBoard?.rank) parts.push(`1m rank #${oneMinBoard.rank}`);
    if (gain3Board?.rank) parts.push(`3m rank #${gain3Board.rank}`);
    if (loss3Board?.rank) parts.push(`3m loss rank #${loss3Board.rank}`);
    return parts.length ? parts.join(" · ") : null;
  }, [forcedCoin, boardRanks]);

  const latestRecentTsMs = useMemo(() => {
    for (const row of compactRecentFallbackRows) {
      const ts = pickTsMs(row);
      if (Number.isFinite(ts)) return ts;
    }
    return null;
  }, [compactRecentFallbackRows]);

  const resolvedEmptyCopy = useMemo(() => {
    if (emptyCopy) return emptyCopy;
    if (!forcedCoin) {
      return feed === "SIGNALS" ? "No evolving signals right now." : "No Pulse detections yet.";
    }
    if (boardContext) {
      return `No live coin signal right now. ${forcedCoin} is still on the board: ${boardContext}.`;
    }
    if (Number.isFinite(latestRecentTsMs)) {
      return `No live coin signal right now. Last ${forcedCoin} signal was ${ageLabel(Math.max(0, nowMs - latestRecentTsMs))} ago.`;
    }
    return `No live coin signal for ${forcedCoin} yet. Board position alone does not always produce an alert.`;
  }, [emptyCopy, forcedCoin, feed, boardContext, latestRecentTsMs, nowMs]);

  const marketPressure = useMemo(
    () => getMarketPressure({ market_pressure: market_pressure ?? effectiveMeta?.market_pressure }),
    [market_pressure, effectiveMeta]
  );

  const priorityEvidence = useMemo(
    () =>
      buildPriorityEvidence({
        activeAlerts: effectiveActiveAlerts,
        recentAlerts: effectiveRecentAlerts,
        nowMs,
      }),
    [effectiveActiveAlerts, effectiveRecentAlerts, nowMs]
  );

  const priorityItems = useMemo(() => {
    if (compact) return [];

    return buildPriorityItems({
      alerts: priorityEvidence,
      gainers1m: gainers_1m,
      gainers3m: gainers_3m,
      losers3m: losers_3m,
      marketPressure,
      nowMs,
      limit: 3,
    });
  }, [
    compact,
    priorityEvidence,
    nowMs,
    gainers_1m,
    gainers_3m,
    losers_3m,
    marketPressure,
  ]);

  const watchlistMetaLabel = watchlistItems.length === 0
    ? "No coins watched"
    : watchlistAttentionRows.length === 0
    ? "Quiet right now"
    : `${watchlistAttentionRows.length} live`;

  const watchlistSectionEl = (
    <section className="bh-alerts-feed-section bh-alerts-feed-section--watchlist" aria-label="Watchlist attention">
      <div className="bh-alerts-feed-section__head">
        <button
          type="button"
          className="bh-section-toggle"
          onClick={() => setWatchlistOpen(v => !v)}
          aria-expanded={watchlistOpen}
          aria-controls="bh-watchlist-body"
        >
          <span className="bh-section-chevron" aria-hidden="true">{watchlistOpen ? "▾" : "▶"}</span>
          <span className="bh-alerts-feed-section__marker" aria-hidden="true">☆</span>
          <span>Watchlist Attention</span>
        </button>
        <div className="bh-alerts-feed-section__meta">{watchlistMetaLabel}</div>
      </div>
      {watchlistOpen ? (
        <div id="bh-watchlist-body">
          {watchlistAttentionRows.length === 0 ? (
            <div className="bh-signal-empty bh-signal-empty--compact">
              {watchlistItems.length === 0
                ? "No coins watched"
                : "No active watchlist alerts right now"}
            </div>
          ) : (
            <div className="bh-signal-list bh-signal-list--watchlist" role="list">
              {watchlistAttentionRows.map((a) => (
                <SignalRow
                  key={`watch:${a.id || `${a.symbol}-${a.type_key}-${pickTsMs(a)}`}`}
                  a={a}
                  nowMs={nowMs}
                  confidenceSemantics="none"
                  onOpenCoinSentiment={onOpenCoinSentiment}
                  isWatchlisted={watchHas(sentimentSymbolForAlert(a))}
                  onToggleWatchlist={toggleAlertWatch}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );

  const evolvingSectionEl = (
    <>
      {compactFallbackToRecent ? (
        <div className="bh-alerts-inline-note">
          No evolving signals right now. Showing Pulse matches.
        </div>
      ) : null}
      <section className="bh-alerts-feed-section bh-alerts-feed-section--evolving" aria-label="Evolving events">
        <div className="bh-alerts-feed-section__head bh-alerts-feed-section__head--all">
          <button
            type="button"
            className="bh-section-toggle"
            onClick={() => setEvolvingOpen(v => !v)}
            aria-expanded={evolvingOpen}
            aria-controls="bh-evolving-body"
          >
            <span className="bh-section-chevron" aria-hidden="true">{evolvingOpen ? "▾" : "▶"}</span>
            <span>{feed === "SIGNALS" ? "Evolving Events" : "Raw Pulse"}</span>
          </button>
          <div className="bh-alerts-feed-section__meta">{displayedRows.length} shown</div>
        </div>
        {evolvingOpen ? (
          <div id="bh-evolving-body" className="bh-signal-list bh-signal-list--primary" role="list">
            {displayedRows.length === 0 ? (
              <div className="bh-signal-empty">{resolvedEmptyCopy}</div>
            ) : (
              displayedRows.map((a) => (
                <SignalRow
                  key={a.id || `${a.symbol}-${a.type_key}-${pickTsMs(a)}`}
                  a={a}
                  nowMs={nowMs}
                  confidenceSemantics={feed === "PULSE" || compactFallbackToRecent ? "interpretation" : "none"}
                  onOpenCoinSentiment={onOpenCoinSentiment}
                  isWatchlisted={watchHas(sentimentSymbolForAlert(a))}
                  onToggleWatchlist={toggleAlertWatch}
                />
              ))
            )}
          </div>
        ) : null}
      </section>
    </>
  );

  return (
    <div className={`bh-alerts-tab ${compact ? "bh-alerts-tab--compact" : ""}`}>
      <div className="bh-alerts-layout">
        <div className="bh-alerts-controls">
          {!hideHeader ? (
            <div className="bh-alerts-feed-head">
              <div className="bh-alerts-feed-title">{allAlertsTitle}</div>

              {!compact ? (
                <div className="bh-alerts-head-actions">
                  <div className="bh-alerts-toggle" role="tablist" aria-label="Signals feed">
                    <button
                      className={`bh-toggle-btn ${feed === "SIGNALS" ? "active" : ""}`}
                      onClick={() => setFeed("SIGNALS")}
                      type="button"
                    >
                      Signals
                    </button>
                    <button
                      className={`bh-toggle-btn ${feed === "PULSE" ? "active" : ""}`}
                      onClick={() => setFeed("PULSE")}
                      type="button"
                    >
                      Pulse
                    </button>
                  </div>

                  <button
                    type="button"
                    className="bh-help-toggle"
                    onClick={() => setShowHelp((v) => !v)}
                    aria-expanded={showHelp}
                  >
                    {showHelp ? "Hide guide" : "Show guide"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {!compact ? (
            <div className="bh-alerts-type-tabs" role="tablist" aria-label="Signal classes">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`bh-type-tab ${typeTab === tab.key ? "active" : ""}`}
                  onClick={() => setTypeTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}

          {!compact ? (
            <div className="bh-alerts-tab-controls">
              <div className="bh-control">
                <div className="bh-control-label">Urgency</div>
                <select className="bh-control-select" value={sev} onChange={(e) => setSev(e.target.value)}>
                  {["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="bh-control">
                <div className="bh-control-label">Order</div>
                <select className="bh-control-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="IMPORTANCE">Most Important</option>
                  <option value="TIME">Newest</option>
                  <option value="URGENCY">Urgency</option>
                  <option value="MAGNITUDE">Biggest Move</option>
                </select>
              </div>

              {!forcedCoin ? (
                <div className="bh-control">
                  <div className="bh-control-label">Coin</div>
                  <select className="bh-control-select" value={coinFilter} onChange={(e) => setCoinFilter(e.target.value)}>
                    {coinOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="bh-control bh-control--mood">
                <div className="bh-control-label">Market Mood</div>
                <MarketMoodCard meta={effectiveMeta} variant="micro" />
              </div>
            </div>
          ) : null}

          {!compact && showHelp ? (
            <div className="bh-alerts-help">
              <div className="bh-alerts-help-title">What you are looking at</div>
              <div className="bh-alerts-help-body">
                <div className="bh-alerts-help-line">
                  <strong>Signals</strong> groups one coin's related detections into Building, Breakout, Moonwalking, and Reversal Risk evolution.
                </div>
                <div className="bh-alerts-help-line">
                  <strong>Pulse</strong> preserves the raw fast detector stream, newest first.
                </div>
                <div className="bh-alerts-help-line">
                  <strong>The Read</strong> turns the evidence into plain language. It describes the setup; it is not a buy or sell instruction.
                </div>
                <div className="bh-alerts-help-line">
                  <strong>Context tags</strong> explain whether a coin is breaking away from the market, moving with it, seeing sampled spot buying or selling, or trading with a wide spread. They add context, not more alerts.
                </div>
                <div className="bh-alerts-help-line">
                  <strong>History</strong> starts showing a measured follow-through rate after 20 comparable completed events.
                </div>
              </div>

              <div className="bh-alerts-glossary" role="list" aria-label="Signal glossary">
                {Object.entries(TYPE_HELP).map(([k, v]) => (
                  <div key={k} className="bh-gloss-item" role="listitem">
                    <span className="bh-gloss-key">{TYPE_DISPLAY[k] || k}</span>
                    <span className="bh-gloss-val">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="bh-alerts-feed">
          {!compact && !forcedCoin ? (
            <PriorityStrip
              items={priorityItems}
              nowMs={nowMs}
              onOpenCoinSentiment={onOpenCoinSentiment}
              marketPressure={marketPressure}
              open={priorityOpen}
              onToggle={() => setPriorityOpen(v => !v)}
            />
          ) : null}

          {!compact && !forcedCoin ? (
            sectionOrder === "watchlist-first" ? (
              <>{watchlistSectionEl}{evolvingSectionEl}</>
            ) : (
              <>{evolvingSectionEl}{watchlistSectionEl}</>
            )
          ) : (
            <div className="bh-signal-list bh-signal-list--primary" role="list">
              {displayedRows.length === 0 ? (
                <div className="bh-signal-empty">{resolvedEmptyCopy}</div>
              ) : (
                displayedRows.map((a) => (
                  <SignalRow
                    key={a.id || `${a.symbol}-${a.type_key}-${pickTsMs(a)}`}
                    a={a}
                    nowMs={nowMs}
                    confidenceSemantics={feed === "PULSE" || compactFallbackToRecent ? "interpretation" : "none"}
                    onOpenCoinSentiment={onOpenCoinSentiment}
                    isWatchlisted={watchHas(sentimentSymbolForAlert(a))}
                    onToggleWatchlist={toggleAlertWatch}
                  />
                ))
              )}
            </div>
          )}

          {!hideFoot ? (
            <div className="bh-signal-foot">
              Click any signal to open the source link or Coinbase Advanced Trade.
            </div>
          ) : null}

          {!compact ? (
            <ProofFooter
              meta={effectiveMeta}
              activeCount={effectiveActiveAlerts.length}
              recentCount={effectiveRecentAlerts.length}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
